import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { 
  getAllMembers, 
  getMemberStats, 
  listenToMembers,
  getMemberPayments,
  getAllPayments,
  getOverdueMembers,
  getUpcomingDueMembers,
  getMembersDueForReminder,
  sendPaymentReminder,
  getPaymentStats,
  getAllContacts,
  updateContactStatus,
  deleteContact
} from './firebase/database';
import { ACTIVITY_LABELS, getRecentActivity } from './firebase/activityLog';
import MembershipForm from './components/MembershipForm';
import DeleteMemberDialog from './components/DeleteMemberDialog';
import PaymentForm from './components/PaymentForm';
import EnhancedPaymentForm from './components/EnhancedPaymentForm';
import MembershipManagementPanel from './components/MembershipManagementPanel';
import SiteContentEditor from './components/SiteContentEditor';

import './App.css';

function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, tenantId, tenant, logOut } = useAuth();
  const isLoading = authLoading;
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const raw = sessionStorage.getItem('dmf:members:listState');
      if (raw) {
        const saved = JSON.parse(raw);
        return saved.activeTab || 'members';
      }
    } catch (_) {}
    return 'members';
  });
  const [showMembershipForm, setShowMembershipForm] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [deleteNotice, setDeleteNotice] = useState('');
  const [recentActivity, setRecentActivity] = useState([]);
  const [isLoadingActivity, setIsLoadingActivity] = useState(false);
  const [members, setMembers] = useState([]);
  const [memberStats, setMemberStats] = useState({
    totalMembers: 0,
    activeMembers: 0,
    newMembersThisMonth: 0,
    revenue: 0
  });
  const [isLoadingMembers, setIsLoadingMembers] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showMemberDetail, setShowMemberDetail] = useState(false);
  
  // Fee Management States
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showEnhancedPaymentForm, setShowEnhancedPaymentForm] = useState(false);
  const [showMembershipManagement, setShowMembershipManagement] = useState(false);
  const [selectedMemberForPayment, setSelectedMemberForPayment] = useState(null);
  const [payments, setPayments] = useState([]);
  const [overdueMembers, setOverdueMembers] = useState([]);
  const [upcomingDueMembers, setUpcomingDueMembers] = useState([]);
  const [reminderMembers, setReminderMembers] = useState([]);
  const [paymentStats, setPaymentStats] = useState({
    totalRevenue: 0,
    monthlyRevenue: 0,
    overdueAmount: 0,
    upcomingAmount: 0
  });
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);

  // Contact Inquiries States
  const [contacts, setContacts] = useState([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [contactStats, setContactStats] = useState({
    totalContacts: 0,
    newContacts: 0,
    trainerInquiries: 0,
    teamInquiries: 0,
    generalInquiries: 0
  });

  // Search and Payment Details States
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [selectedMemberPayments, setSelectedMemberPayments] = useState([]);
  const [isLoadingMemberPayments, setIsLoadingMemberPayments] = useState(false);
  
  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Tenant admin must have tenantId; if missing, redirect once to login (profile not set up)
  useEffect(() => {
    if (authLoading || !user) return;
    if (!tenantId) {
      navigate('/adminlogin', { replace: true });
    }
  }, [authLoading, user, tenantId, navigate]);

  // Handle navigation state from MemberDetails
  useEffect(() => {
    if (location.state) {
      console.log('Dashboard received navigation state:', location.state);
      const { openPaymentForm, openEnhancedPaymentForm, openMembershipManagement, selectedMember, activeTab, notice } = location.state;

      if (activeTab) {
        setActiveTab(activeTab);
      }

      // Confirmation passed back after deleting a member from their profile
      if (notice) {
        setDeleteNotice(notice);
        setTimeout(() => setDeleteNotice(''), 5000);
        navigate(location.pathname, { replace: true });
      }

      if (selectedMember) {
        setSelectedMemberForPayment(selectedMember);
      }
      
      if (openPaymentForm) {
        console.log('Opening payment form for member:', selectedMember);
        setShowPaymentForm(true);
        // Clear the state to prevent re-triggering
        navigate(location.pathname, { replace: true });
      }
      
      if (openEnhancedPaymentForm) {
        console.log('Opening enhanced payment form for member:', selectedMember);
        setShowEnhancedPaymentForm(true);
        // Clear the state to prevent re-triggering
        navigate(location.pathname, { replace: true });
      }
      
      if (openMembershipManagement) {
        console.log('Opening membership management for member:', selectedMember);
        setShowMembershipManagement(true);
        // Clear the state to prevent re-triggering
        navigate(location.pathname, { replace: true });
      }
    }
  }, [location.state, location.pathname, navigate]);

  // Fetch members data
  useEffect(() => {
    if (!user || !tenantId) return;

    const fetchMembersData = async () => {
      try {
        setIsLoadingMembers(true);
        const membersResult = await getAllMembers(tenantId);
        if (membersResult.success) {
          const membersData = membersResult.members || [];
          setMembers(membersData);
          setFilteredMembers(membersData);
        }
        const statsResult = await getMemberStats(tenantId);
        if (statsResult.success) {
          setMemberStats(statsResult.stats);
        }
      } catch (error) {
        console.error('Error fetching members data:', error);
      } finally {
        setIsLoadingMembers(false);
      }
    };

    const unsubscribeMembers = listenToMembers(tenantId, (data) => {
      if (data && data.members) {
        setMembers(data.members);
        setFilteredMembers(data.members);
      }
    });

    fetchMembersData();
    return () => { unsubscribeMembers(); };
  }, [user, tenantId]);

  const restoredListStateRef = useRef(false);
  // Restore members list state when coming back to Dashboard (only once)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('dmf:members:listState');
      if (!raw) return;
      const saved = JSON.parse(raw);

      // If not yet restored and we're not already on members, switch once
      if (!restoredListStateRef.current && activeTab !== 'members') {
        setActiveTab('members');
        return; // wait for members tab to render
      }

      if (activeTab !== 'members' || isLoadingMembers || restoredListStateRef.current) return;

      // Restore search/filter
      if (typeof saved.searchTerm === 'string') {
        handleSearchChange({ target: { value: saved.searchTerm } });
      }
      // Restore scroll positions after paint
      setTimeout(() => {
        if (typeof saved.scrollY === 'number') {
          window.scrollTo(0, saved.scrollY);
        }
        const table = document.querySelector('.members-table-container');
        if (table && typeof saved.tableScrollTop === 'number') {
          table.scrollTop = saved.tableScrollTop;
        }
      }, 0);

      restoredListStateRef.current = true;
      // Clear so future tab clicks (e.g., Overview) don't auto-switch back
      sessionStorage.removeItem('dmf:members:listState');
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isLoadingMembers]);

  // Fetch fee management data function
  const fetchFeeManagementData = async () => {
    if (!tenantId) return;
    try {
      setIsLoadingPayments(true);
      const statsResult = await getPaymentStats(tenantId);
      if (statsResult.success) setPaymentStats(statsResult.stats);
      const overdueResult = await getOverdueMembers(tenantId);
      if (overdueResult.success) setOverdueMembers(overdueResult.members);
      const upcomingResult = await getUpcomingDueMembers(tenantId);
      if (upcomingResult.success) setUpcomingDueMembers(upcomingResult.members);
      const reminderResult = await getMembersDueForReminder(tenantId);
      if (reminderResult.success) setReminderMembers(reminderResult.members);
      const paymentsResult = await getAllPayments(tenantId);
      if (paymentsResult.success) setPayments(paymentsResult.payments);
    } catch (error) {
      console.error('Error fetching fee management data:', error);
    } finally {
      setIsLoadingPayments(false);
    }
  };

  useEffect(() => {
    if (!user || !tenantId) return;
    fetchFeeManagementData();
  }, [user, tenantId]);

  // Recent changes made to any member (audit feed)
  useEffect(() => {
    if (!user || !tenantId) return;
    const fetchRecentActivity = async () => {
      try {
        setIsLoadingActivity(true);
        const result = await getRecentActivity(tenantId, 10);
        if (result.success) setRecentActivity(result.activity);
      } catch (error) {
        console.error('Error fetching recent activity:', error);
      } finally {
        setIsLoadingActivity(false);
      }
    };
    fetchRecentActivity();
  }, [user, tenantId, activeTab]);

  // Fetch contacts data
  useEffect(() => {
    if (!user || !tenantId) return;
    const fetchContactsData = async () => {
      try {
        setIsLoadingContacts(true);
        const contactsResult = await getAllContacts(tenantId);
        if (contactsResult.success) {
          const contactsData = contactsResult.contacts || [];
          setContacts(contactsData);
          setContactStats({
            totalContacts: contactsData.length,
            newContacts: contactsData.filter(c => c.status === 'new').length,
            trainerInquiries: contactsData.filter(c => c.inquiryType === 'trainer').length,
            teamInquiries: contactsData.filter(c => c.inquiryType === 'team').length,
            generalInquiries: contactsData.filter(c => c.inquiryType === 'general').length
          });
        }
      } catch (error) {
        console.error('Error fetching contacts data:', error);
      } finally {
        setIsLoadingContacts(false);
      }
    };
    fetchContactsData();
  }, [user, tenantId]);

  const handleLogout = async () => {
    try {
      await logOut();
      navigate('/adminlogin');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleAddMember = () => {
    setShowMembershipForm(true);
  };

  const handleFormSuccess = () => {
    // Refresh dashboard data when new member is added
    console.log('Member added successfully!');
    // The real-time listener will automatically update the data
  };

  const handleMemberClick = (member) => {
    // Persist members list UI state before navigating
    try {
      const table = document.querySelector('.members-table-container');
      const state = {
        activeTab: 'members',
        searchTerm,
        scrollY: window.scrollY || 0,
        tableScrollTop: table && typeof table.scrollTop === 'number' ? table.scrollTop : 0
      };
      sessionStorage.setItem('dmf:members:listState', JSON.stringify(state));
    } catch (_) {}

    // Navigate to member details page (pass tenantId for scoped data)
    navigate(`/memberdetails/${member.membershipId}`, { state: { tenantId } });
  };

  const closeMemberDetail = () => {
    setSelectedMember(null);
    setShowMemberDetail(false);
  };

  // Fee Management Handlers
  const handleRecordPayment = (member) => {
    setSelectedMemberForPayment(member);
    setShowPaymentForm(true);
  };

  const handleEnhancedPayment = (member) => {
    setSelectedMemberForPayment(member);
    setShowEnhancedPaymentForm(true);
  };

  const handleMembershipManagement = (member) => {
    setSelectedMemberForPayment(member);
    setShowMembershipManagement(true);
  };

  const handlePaymentSuccess = () => {
    setShowPaymentForm(false);
    setSelectedMemberForPayment(null);
    // Refresh fee management data
    fetchFeeManagementData();
  };

  // Contact Management Handlers
  const handleUpdateContactStatus = async (contactId, newStatus) => {
    if (!tenantId) return;
    try {
      const result = await updateContactStatus(tenantId, contactId, newStatus);
      if (result.success) {
        const contactsResult = await getAllContacts(tenantId);
        if (contactsResult.success) {
          const contactsData = contactsResult.contacts || [];
          setContacts(contactsData);
          setContactStats({
            totalContacts: contactsData.length,
            newContacts: contactsData.filter(c => c.status === 'new').length,
            trainerInquiries: contactsData.filter(c => c.inquiryType === 'trainer').length,
            teamInquiries: contactsData.filter(c => c.inquiryType === 'team').length,
            generalInquiries: contactsData.filter(c => c.inquiryType === 'general').length
          });
        }
      }
    } catch (error) {
      console.error('Error updating contact status:', error);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!tenantId) return;
    if (!window.confirm('Are you sure you want to delete this contact inquiry?')) return;
    try {
      const result = await deleteContact(tenantId, contactId);
      if (result.success) {
        const contactsResult = await getAllContacts(tenantId);
        if (contactsResult.success) {
          const contactsData = contactsResult.contacts || [];
          setContacts(contactsData);
          setContactStats({
            totalContacts: contactsData.length,
            newContacts: contactsData.filter(c => c.status === 'new').length,
            trainerInquiries: contactsData.filter(c => c.inquiryType === 'trainer').length,
            teamInquiries: contactsData.filter(c => c.inquiryType === 'team').length,
            generalInquiries: contactsData.filter(c => c.inquiryType === 'general').length
          });
        }
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
    }
  };

  // Search functionality
  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when search changes
    
    if (!term.trim()) {
      setFilteredMembers(members);
    } else {
      const filtered = members.filter(member => 
        member.fullName?.toLowerCase().includes(term.toLowerCase()) ||
        member.email?.toLowerCase().includes(term.toLowerCase()) ||
        member.phone?.includes(term) ||
        member.membershipId?.toLowerCase().includes(term.toLowerCase())
      );
      setFilteredMembers(filtered);
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedMembers = filteredMembers.slice(startIndex, endIndex);

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top of table when page changes
    const table = document.querySelector('.members-table-container');
    if (table) {
      table.scrollTop = 0;
    }
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when items per page changes
  };

  // Payment details functionality
  const handleViewPaymentDetails = async (member) => {
    if (!tenantId) return;
    try {
      setIsLoadingMemberPayments(true);
      setSelectedMemberPayments([]);
      const paymentsResult = await getMemberPayments(tenantId, member.id);
      if (paymentsResult.success) {
        setSelectedMemberPayments(paymentsResult.payments);
        setShowPaymentDetails(true);
      } else {
        alert('Failed to load payment details: ' + paymentsResult.message);
      }
    } catch (error) {
      console.error('Error loading payment details:', error);
      alert('Error loading payment details');
    } finally {
      setIsLoadingMemberPayments(false);
    }
  };

  const closePaymentDetails = () => {
    setShowPaymentDetails(false);
    setSelectedMemberPayments([]);
  };

  const handleSendReminder = async (memberId, reminderType = 'due') => {
    if (!tenantId) return;
    try {
      const result = await sendPaymentReminder(tenantId, memberId, reminderType);
      if (result.success) {
        let message = `Payment reminder sent successfully!\n\nEmail sent to: ${result.sentTo}\nSubject: ${result.subject}`;
        
        if (result.smsSent) {
          message += `\n\nSMS sent to: ${result.smsTo}`;
        } else if (result.smsTo) {
          message += `\n\nSMS failed to send to: ${result.smsTo}`;
        }
        
        alert(message);
      } else {
        alert('Failed to send reminder: ' + result.message);
      }
    } catch (error) {
      alert('Error sending reminder: ' + error.message);
    }
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    
    const due = new Date(dueDate);
    const today = new Date();
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  const getPaymentStatusColor = (dueDate) => {
    const daysUntilDue = getDaysUntilDue(dueDate);
    
    if (daysUntilDue < 0) return '#e53e3e'; // Red for overdue
    return '#d69e2e'; // Yellow for due
  };

  const getPaymentStatusText = (dueDate) => {
    const daysUntilDue = getDaysUntilDue(dueDate);
    
    if (daysUntilDue < 0) return 'Overdue';
    return 'Due';
  };

  const getGoalDisplayName = (goalKey) => {
    const goalNames = {
      fatReduction: 'Fat Reduction and Weight Loss Strategies for Health',
      energyEndurance: 'Energy & Endurance',
      muscleStrength: 'Enhance muscle strength while maintaining a sleek and formidable physique',
      timelessYouth: 'Endless energy, timeless youth, and a radiant spirit',
      digestiveHealth: 'A Healthier Digestive System',
      sculptedPhysique: 'Sculpted physique',
      challengeMuscles: 'Challenge muscles',
      sweatFit: 'Sweat Fit',
      allOfAbove: 'All of the above'
    };
    return goalNames[goalKey] || goalKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };

  // Contact Helper Functions
  const getInquiryTypeDisplay = (inquiryType) => {
    const typeNames = {
      'general': 'General Inquiry',
      'trainer': 'Become a Trainer',
      'team': 'Join Our Team'
    };
    return typeNames[inquiryType] || inquiryType;
  };

  const getInquiryTypeColor = (inquiryType) => {
    const colors = {
      'general': '#6b7280',
      'trainer': '#3b82f6',
      'team': '#e53e3e'
    };
    return colors[inquiryType] || '#6b7280';
  };

  const getStatusDisplay = (status) => {
    const statusNames = {
      'new': 'New',
      'read': 'Read',
      'responded': 'Responded',
      'closed': 'Closed'
    };
    return statusNames[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': '#e53e3e',
      'read': '#f59e0b',
      'responded': '#10b981',
      'closed': '#6b7280'
    };
    return colors[status] || '#6b7280';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (!user) {
    return null; // Will redirect to login
  }

  return (
    <div className="dashboard-container">
      {/* Header */}
      <header className="dashboard-header">
        <div className="dashboard-header-content">
          <div className="dashboard-logo">
            {tenant?.branding?.logoUrl ? (
              <img src={tenant.branding.logoUrl} alt="" className="dashboard-logo-img" />
            ) : (
              <div className="logo-icon">💪</div>
            )}
            <span className="logo-text">{tenant?.branding?.siteName || tenant?.name || 'Dashboard'}</span>
            <span className="dashboard-subtitle">Admin Dashboard</span>
          </div>
          <div className="dashboard-user-info">
            <span className="user-email">{user.email}</span>
            <button onClick={handleLogout} className="logout-btn">
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="dashboard-nav">
        <button 
          className={`nav-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button 
          className={`nav-tab ${activeTab === 'members' ? 'active' : ''}`}
          onClick={() => setActiveTab('members')}
        >
          👥 Members
        </button>
        <button 
          className={`nav-tab ${activeTab === 'classes' ? 'active' : ''}`}
          onClick={() => setActiveTab('classes')}
        >
          🏋️ Classes
        </button>
        <button 
          className={`nav-tab ${activeTab === 'trainers' ? 'active' : ''}`}
          onClick={() => setActiveTab('trainers')}
        >
          🧑‍🏫 Trainers
        </button>
        <button 
          className={`nav-tab ${activeTab === 'payments' ? 'active' : ''}`}
          onClick={() => setActiveTab('payments')}
        >
          💰 Payments
        </button>
        <button 
          className={`nav-tab ${activeTab === 'contacts' ? 'active' : ''}`}
          onClick={() => setActiveTab('contacts')}
        >
          📧 Contact Inquiries
        </button>
        <button 
          className={`nav-tab ${activeTab === 'website' ? 'active' : ''}`}
          onClick={() => setActiveTab('website')}
        >
          🌐 Website
        </button>
        <button 
          className={`nav-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          ⚙️ Settings
        </button>
      </nav>

      {/* Main Content */}
      <main className="dashboard-main">
        {activeTab === 'overview' && (
          <div className="dashboard-content">
            <h2 className="dashboard-title">Dashboard Overview</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">👥</div>
                <div className="stat-info">
                  <h3>Total Members</h3>
                  <p className="stat-number">
                    {isLoadingMembers ? '...' : memberStats.totalMembers || 0}
                  </p>
                  <span className="stat-change positive">
                    {(memberStats.newMembersThisMonth || 0) > 0 ? `+${memberStats.newMembersThisMonth} this month` : 'No new members'}
                  </span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🏋️</div>
                <div className="stat-info">
                  <h3>Active Members</h3>
                  <p className="stat-number">
                    {isLoadingMembers ? '...' : memberStats.activeMembers || 0}
                  </p>
                  <span className="stat-change positive">
                    {(paymentStats.pausedMembers || 0) > 0
                      ? `${paymentStats.pausedMembers} paused`
                      : (memberStats.activeMembers || 0) > 0 ? 'Active' : 'No active members'}
                  </span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">💰</div>
                <div className="stat-info">
                  <h3>Monthly Revenue</h3>
                  <p className="stat-number">
                    ₹{isLoadingPayments ? '...' : paymentStats.monthlyRevenue?.toLocaleString() || 0}
                  </p>
                  <span className="stat-change positive">
                    Total: ₹{paymentStats.totalRevenue?.toLocaleString() || 0}
                  </span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">⚠️</div>
                <div className="stat-info">
                  <h3>Overdue Amount</h3>
                  <p className="stat-number">
                    ₹{isLoadingPayments ? '...' : paymentStats.overdueAmount?.toLocaleString() || 0}
                  </p>
                  <span className="stat-change negative">
                    {overdueMembers.length} members overdue
                  </span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">📧</div>
                <div className="stat-info">
                  <h3>Contact Inquiries</h3>
                  <p className="stat-number">
                    {isLoadingContacts ? '...' : contactStats.totalContacts || 0}
                  </p>
                  <span className="stat-change positive">
                    {contactStats.newContacts || 0} new inquiries
                  </span>
                </div>
              </div>
            </div>

            <div className="dashboard-sections">
              <div className="section-card">
                <h3>Quick Actions</h3>
                <div className="quick-actions">
                  <button className="action-btn" onClick={handleAddMember}>
                    <span className="action-icon">➕</span>
                    Add New Member
                  </button>
                  <button className="action-btn">
                    <span className="action-icon">📅</span>
                    Schedule Class
                  </button>
                  <button className="action-btn">
                    <span className="action-icon">📊</span>
                    View Reports
                  </button>
                  <button className="action-btn">
                    <span className="action-icon">🔔</span>
                    Send Notifications
                  </button>
                </div>
              </div>

              {/* Recent changes across all members */}
              <div className="section-card">
                <h3>🕒 Recent Member Changes</h3>
                {isLoadingActivity ? (
                  <p className="activity-feed-empty">Loading recent changes...</p>
                ) : recentActivity.length === 0 ? (
                  <p className="activity-feed-empty">
                    No member changes recorded yet. Edits, pauses and resumes will appear here.
                  </p>
                ) : (
                  <ul className="activity-feed">
                    {recentActivity.map((entry) => {
                      const meta = ACTIVITY_LABELS[entry.type] || { icon: '📝', label: 'Change' };
                      const entryDate = entry.performedAt ? new Date(entry.performedAt) : null;

                      return (
                        <li key={entry.id} className={`activity-feed-item ${entry.type}`}>
                          <span className="activity-feed-icon">{meta.icon}</span>
                          <div className="activity-feed-body">
                            <div className="activity-feed-title">
                              <strong>{entry.memberName || 'Member'}</strong> — {meta.label}
                            </div>
                            {entry.summary && (
                              <div className="activity-feed-summary">{entry.summary}</div>
                            )}
                            <div className="activity-feed-meta">
                              By {entry.performedBy || 'system'}
                              {entryDate && !isNaN(entryDate.getTime())
                                ? ` · ${entryDate.toLocaleString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit'
                                  })}`
                                : ''}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'members' && !selectedMemberForPayment && (
          <div className="dashboard-content">
            <h2 className="dashboard-title">Member Management</h2>

            {deleteNotice && (
              <div className="member-delete-notice">✅ {deleteNotice}</div>
            )}

            {isLoadingMembers ? (
              <div className="loading-section">
                <div className="loading-spinner"></div>
                <p>Loading members...</p>
              </div>
            ) : members.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">👥</div>
                <h3>No Members Yet</h3>
                <p>Start by adding your first member!</p>
                <button className="action-btn" onClick={handleAddMember}>
                  <span className="action-icon">➕</span>
                  Add First Member
                </button>
              </div>
            ) : (
              <div className="members-section">
                <div className="members-header">
                  <div className="members-header-left">
                    <h3>All Members ({filteredMembers.length})</h3>
                    <div className="search-container">
                      <input
                        type="text"
                        placeholder="Search by name, email, phone, or ID..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="search-input"
                      />
                      <span className="search-icon">🔍</span>
                    </div>
                  </div>
                  <button className="action-btn" onClick={handleAddMember}>
                    <span className="action-icon">➕</span>
                    Add New Member
                  </button>
                </div>
                
                <div className="members-table-container">
                  <table className="members-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Phone</th>
                        <th>Age</th>
                        <th>Duration</th>
                        <th>Fees</th>
                        <th>Last Payment</th>
                        <th>Next Due</th>
                        <th>Payment Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedMembers.map((member, index) => (
                        <tr key={member.membershipId || index} className="member-row" onClick={() => handleMemberClick(member)}>
                          <td>
                            <div className="member-name-cell">
                              <span className="member-name">{member.fullName || 'Unknown'}</span>
                              {member.membershipStatus === 'paused' && (
                                <span
                                  title={`Membership paused${member.pauseStartDate ? ` on ${new Date(member.pauseStartDate).toLocaleDateString()}` : ''}`}
                                  style={{ marginLeft: '0.4rem' }}
                                >
                                  ⏸️
                                </span>
                              )}
                            </div>
                          </td>
                          <td>{member.phone || 'N/A'}</td>
                          <td>{member.age || 'N/A'}</td>
                          <td>{member.membershipDuration || 'N/A'}</td>
                          <td>{member.fees || 'N/A'}</td>
                          <td>{member.lastPaymentDate ? new Date(member.lastPaymentDate).toLocaleDateString() : 'No payments'}</td>
                          <td>{member.nextDueDate ? new Date(member.nextDueDate).toLocaleDateString() : 'Not set'}</td>
                          <td>
                            <span 
                              className="payment-status-badge"
                              style={{ backgroundColor: getPaymentStatusColor(member.nextDueDate) }}
                            >
                              {getPaymentStatusText(member.nextDueDate)}
                            </span>
                          </td>
                          <td>
                            {/* stopPropagation so the row click doesn't navigate */}
                            <button
                              className="member-delete-btn"
                              title={`Delete ${member.fullName || 'member'}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setMemberToDelete(member);
                              }}
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {filteredMembers.length > 0 && (
                  <div className="pagination-container">
                    <div className="pagination-info">
                      <span>
                        Showing {startIndex + 1} to {Math.min(endIndex, filteredMembers.length)} of {filteredMembers.length} members
                      </span>
                      <div className="items-per-page">
                        <label>Items per page:</label>
                        <select value={itemsPerPage} onChange={handleItemsPerPageChange}>
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>
                    </div>
                    <div className="pagination-controls">
                      <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                      >
                        «« First
                      </button>
                      <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        « Previous
                      </button>
                      <div className="page-numbers">
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter(page => {
                            // Show first page, last page, current page, and pages around current
                            return (
                              page === 1 ||
                              page === totalPages ||
                              (page >= currentPage - 2 && page <= currentPage + 2)
                            );
                          })
                          .map((page, index, array) => {
                            // Add ellipsis if there's a gap
                            const showEllipsisBefore = index > 0 && array[index - 1] !== page - 1;
                            return (
                              <React.Fragment key={page}>
                                {showEllipsisBefore && <span className="pagination-ellipsis">...</span>}
                                <button
                                  className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                                  onClick={() => handlePageChange(page)}
                                >
                                  {page}
                                </button>
                              </React.Fragment>
                            );
                          })}
                      </div>
                      <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next »
                      </button>
                      <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        Last »»
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}



        {activeTab === 'classes' && (
          <div className="dashboard-content">
            <h2 className="dashboard-title">Class Management</h2>
            <p>Class management features coming soon...</p>
          </div>
        )}

        {activeTab === 'trainers' && (
          <div className="dashboard-content">
            <h2 className="dashboard-title">Trainer Management</h2>
            <p>Trainer management features coming soon...</p>
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="dashboard-content">
            <h2 className="dashboard-title">Payment Management</h2>
            
            {/* Payment Statistics */}
            <div className="payment-stats-grid">
              <div className="payment-stat-card">
                <div className="stat-icon">💰</div>
                <div className="stat-info">
                  <h3>Total Revenue</h3>
                  <p className="stat-number">₹{paymentStats.totalRevenue?.toLocaleString() || 0}</p>
                </div>
              </div>
              <div className="payment-stat-card">
                <div className="stat-icon">📈</div>
                <div className="stat-info">
                  <h3>Monthly Revenue</h3>
                  <p className="stat-number">₹{paymentStats.monthlyRevenue?.toLocaleString() || 0}</p>
                </div>
              </div>
              <div className="payment-stat-card">
                <div className="stat-icon">⚠️</div>
                <div className="stat-info">
                  <h3>Overdue Amount</h3>
                  <p className="stat-number">₹{paymentStats.overdueAmount?.toLocaleString() || 0}</p>
                </div>
              </div>
              <div className="payment-stat-card">
                <div className="stat-icon">⏰</div>
                <div className="stat-info">
                  <h3>Upcoming Due</h3>
                  <p className="stat-number">₹{paymentStats.upcomingAmount?.toLocaleString() || 0}</p>
                </div>
              </div>
            </div>

            {/* Reminder Members Section (2 days before due) */}
            {reminderMembers.length > 0 && (
              <div className="payment-section">
                <div className="section-header">
                  <h3>🔔 Reminder Due Members ({reminderMembers.length})</h3>
                  <button 
                    className="action-btn"
                    onClick={() => {
                      reminderMembers.forEach(member => handleSendReminder(member.id, 'reminder'));
                    }}
                  >
                    🔔 Send All Reminders
                  </button>
                </div>
                <div className="members-grid">
                  {reminderMembers.map((member, index) => (
                    <div key={member.id || index} className="member-card reminder">
                      <div className="member-header">
                        <div className="member-avatar">
                          {member.fullName ? member.fullName.charAt(0).toUpperCase() : '?'}
                        </div>
                        <div className="member-info">
                          <h4>{member.fullName || 'Unknown Member'}</h4>
                          <p>{member.membershipLevel} • {member.membershipDuration} • {member.sex ? member.sex.charAt(0).toUpperCase() + member.sex.slice(1) : 'N/A'}</p>
                        </div>
                      </div>
                      <div className="member-details">
                        <p><strong>Fees:</strong> {member.fees}</p>
                        <p><strong>Due Date:</strong> {new Date(member.nextDueDate).toLocaleDateString()}</p>
                        <p><strong>Payment Status:</strong> 
                          <span 
                            style={{ 
                              backgroundColor: getPaymentStatusColor(member.nextDueDate),
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '0.8rem',
                              fontWeight: '500',
                              marginLeft: '8px'
                            }}
                          >
                            {getPaymentStatusText(member.nextDueDate)}
                          </span>
                        </p>
                      </div>
                      <div className="member-actions">
                        <button className="payment-btn" onClick={() => handleRecordPayment(member)}>
                          💰 Record Payment
                        </button>
                        <button className="reminder-btn" onClick={() => handleSendReminder(member.id, 'reminder')}>
                          🔔 Send Reminder
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Payments Section */}
            <div className="payment-section">
              <div className="section-header">
                <h3>📊 Recent Payments</h3>
              </div>
              {isLoadingPayments ? (
                <div className="loading-section">
                  <div className="loading-spinner"></div>
                  <p>Loading payments...</p>
                </div>
              ) : payments.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">💰</div>
                  <h3>No Payments Yet</h3>
                  <p>Start recording payments to see them here!</p>
                </div>
              ) : (
                <div className="payments-table-container">
                  <table className="payments-table">
                    <thead>
                      <tr>
                        <th>Member</th>
                        <th>Amount</th>
                        <th>Payment Method</th>
                        <th>Payment Date</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.slice(0, 10).map((payment, index) => (
                        <tr key={payment.id || index}>
                          <td>
                            <div className="member-name-cell">
                              <div className="member-avatar-small">
                                {payment.memberName ? payment.memberName.charAt(0).toUpperCase() : '?'}
                              </div>
                              <span>{payment.memberName || 'Unknown'}</span>
                            </div>
                          </td>
                          <td className="amount-cell">₹{payment.amount?.toLocaleString() || 0}</td>
                          <td>
                            <span className={`payment-method-badge ${payment.paymentMethod}`}>
                              {payment.paymentMethod}
                            </span>
                          </td>
                          <td>{new Date(payment.paymentDate).toLocaleDateString()}</td>
                          <td>{payment.notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'contacts' && (
          <div className="dashboard-content">
            <h2 className="dashboard-title">Contact Inquiries</h2>
            
            {/* Contact Statistics */}
            <div className="payment-stats-grid">
              <div className="payment-stat-card">
                <div className="stat-icon">📧</div>
                <div className="stat-info">
                  <h3>Total Inquiries</h3>
                  <p className="stat-number">{contactStats.totalContacts || 0}</p>
                </div>
              </div>
              <div className="payment-stat-card">
                <div className="stat-icon">🆕</div>
                <div className="stat-info">
                  <h3>New Inquiries</h3>
                  <p className="stat-number">{contactStats.newContacts || 0}</p>
                </div>
              </div>
              <div className="payment-stat-card">
                <div className="stat-icon">🧑‍🏫</div>
                <div className="stat-info">
                  <h3>Trainer Inquiries</h3>
                  <p className="stat-number">{contactStats.trainerInquiries || 0}</p>
                </div>
              </div>
              <div className="payment-stat-card">
                <div className="stat-icon">👥</div>
                <div className="stat-info">
                  <h3>Team Inquiries</h3>
                  <p className="stat-number">{contactStats.teamInquiries || 0}</p>
                </div>
              </div>
            </div>

            {/* Contact Inquiries List */}
            <div className="payment-section">
              <div className="section-header">
                <h3>📧 All Contact Inquiries</h3>
              </div>
              {isLoadingContacts ? (
                <div className="loading-section">
                  <div className="loading-spinner"></div>
                  <p>Loading contact inquiries...</p>
                </div>
              ) : contacts.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📧</div>
                  <h3>No Contact Inquiries Yet</h3>
                  <p>Contact form submissions will appear here!</p>
                </div>
              ) : (
                <div className="members-table-container">
                  <table className="members-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Inquiry Type</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.map((contact, index) => (
                        <tr key={contact.id || index}>
                          <td>
                            <div className="member-name-cell">
                              <div className="member-avatar-small">
                                {contact.name ? contact.name.charAt(0).toUpperCase() : '?'}
                              </div>
                              <span>{contact.name || 'N/A'}</span>
                            </div>
                          </td>
                          <td>{contact.email || 'N/A'}</td>
                          <td>{contact.phone || 'N/A'}</td>
                          <td>
                            <span 
                              className="inquiry-type-badge"
                              style={{ 
                                backgroundColor: getInquiryTypeColor(contact.inquiryType),
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '12px',
                                fontSize: '0.8rem',
                                fontWeight: '500'
                              }}
                            >
                              {getInquiryTypeDisplay(contact.inquiryType)}
                            </span>
                          </td>
                          <td>
                            <span 
                              className="status-badge"
                              style={{ 
                                backgroundColor: getStatusColor(contact.status || 'new'),
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '12px',
                                fontSize: '0.8rem',
                                fontWeight: '500'
                              }}
                            >
                              {getStatusDisplay(contact.status || 'new')}
                            </span>
                          </td>
                          <td>{formatDate(contact.submissionDate)}</td>
                          <td>
                            <div className="action-buttons">
                              <button 
                                className="view-btn"
                                onClick={() => {
                                  alert(`Message: ${contact.message || 'No message provided'}`);
                                }}
                              >
                                👁️ View
                              </button>
                              <select 
                                value={contact.status || 'new'}
                                onChange={(e) => handleUpdateContactStatus(contact.id, e.target.value)}
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  border: '1px solid #ddd',
                                  fontSize: '0.8rem'
                                }}
                              >
                                <option value="new">New</option>
                                <option value="read">Read</option>
                                <option value="responded">Responded</option>
                                <option value="closed">Closed</option>
                              </select>
                              <button 
                                className="view-btn"
                                onClick={() => handleDeleteContact(contact.id)}
                                style={{ backgroundColor: '#e53e3e' }}
                              >
                                🗑️ Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'website' && tenantId && tenant && (
          <SiteContentEditor
            tenant={tenant}
            tenantId={tenantId}
            onSaved={() => {}}
          />
        )}

        {activeTab === 'settings' && (
          <div className="dashboard-content">
            <h2 className="dashboard-title">Settings</h2>
            <p>Settings features coming soon...</p>
          </div>
        )}
      </main>

      {/* Membership Form Modal */}
      {memberToDelete && tenantId && (
        <DeleteMemberDialog
          tenantId={tenantId}
          member={memberToDelete}
          onCancel={() => setMemberToDelete(null)}
          onDeleted={(result) => {
            setMemberToDelete(null);
            setDeleteNotice(result.message);
            // The members list is a live listener, so it refreshes itself
            fetchFeeManagementData();
            setTimeout(() => setDeleteNotice(''), 5000);
          }}
        />
      )}

      {showMembershipForm && tenantId && (
        <MembershipForm
          tenantId={tenantId}
          onClose={() => setShowMembershipForm(false)}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Payment Form Modal */}
      {showPaymentForm && selectedMemberForPayment && tenantId && (
        <PaymentForm
          tenantId={tenantId}
          member={selectedMemberForPayment}
          onClose={() => {
            setShowPaymentForm(false);
            setSelectedMemberForPayment(null);
          }}
          onSuccess={handlePaymentSuccess}
        />
      )}

      {/* Enhanced Payment Form Modal */}
      {showEnhancedPaymentForm && selectedMemberForPayment && tenantId && (
        <EnhancedPaymentForm
          tenantId={tenantId}
          member={selectedMemberForPayment}
          onClose={() => {
            setShowEnhancedPaymentForm(false);
            setSelectedMemberForPayment(null);
          }}
          onSuccess={handlePaymentSuccess}
        />
      )}

      {/* Membership Management Panel Modal */}
      {showMembershipManagement && selectedMemberForPayment && tenantId && (
        <MembershipManagementPanel
          tenantId={tenantId}
          member={selectedMemberForPayment}
          onClose={() => {
            setShowMembershipManagement(false);
            setSelectedMemberForPayment(null);
          }}
          onSuccess={handlePaymentSuccess}
        />
      )}

      
      


      {/* Member Detail Modal */}
      {showMemberDetail && selectedMember && (
        <div className="member-detail-overlay" onClick={closeMemberDetail}>
          <div className="member-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="member-detail-header">
              <h2>Member Details</h2>
              <button className="close-btn" onClick={closeMemberDetail}>×</button>
            </div>
            
            <div className="member-detail-content">
              <div className="member-detail-avatar">
                <div className="member-avatar-large">
                  {selectedMember.fullName ? selectedMember.fullName.charAt(0).toUpperCase() : '?'}
                </div>
                <div className="member-detail-info">
                  <h2 className="member-detail-name">{selectedMember.fullName || 'Unknown Member'}</h2>
                  <div className="member-detail-status">
                    <span className={`status-badge ${selectedMember.status || 'active'}`}>
                      {selectedMember.status || 'Active'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="member-detail-info">
                <div className="detail-section">
                  <h3>Personal Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Full Name:</label>
                      <span>{selectedMember.fullName || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Email:</label>
                      <span>{selectedMember.email || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Phone:</label>
                      <span>{selectedMember.phone || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Address:</label>
                      <span>{selectedMember.fullAddress || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Date of Birth:</label>
                      <span>{selectedMember.dateOfBirth || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Age:</label>
                      <span>{selectedMember.age || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Sex:</label>
                      <span>{selectedMember.sex ? selectedMember.sex.charAt(0).toUpperCase() + selectedMember.sex.slice(1) : 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Height:</label>
                      <span>{selectedMember.height || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Weight:</label>
                      <span>{selectedMember.weight || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h3>Membership Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Membership Level:</label>
                      <span>{selectedMember.membershipLevel || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Duration:</label>
                      <span>{selectedMember.membershipDuration || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Fees:</label>
                      <span>{selectedMember.fees || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Payment Method:</label>
                      <span>{selectedMember.paymentMethod || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Registration Date:</label>
                      <span>{selectedMember.registrationDate || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Membership ID:</label>
                      <span>{selectedMember.membershipId || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Next Due Date:</label>
                      <span>{selectedMember.nextDueDate ? new Date(selectedMember.nextDueDate).toLocaleDateString() : 'Not set'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Payment Status:</label>
                      <span 
                        style={{ 
                          backgroundColor: getPaymentStatusColor(selectedMember.nextDueDate),
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '0.8rem',
                          fontWeight: '500'
                        }}
                      >
                        {getPaymentStatusText(selectedMember.nextDueDate)}
                      </span>
                    </div>
                  </div>
                </div>

                {selectedMember.fitnessGoals && (
                  <div className="detail-section">
                    <h3>Fitness Goals</h3>
                    <div className="fitness-goals-list">
                      {Object.entries(selectedMember.fitnessGoals).map(([goal, isSelected]) => (
                        isSelected && (
                          <div key={goal} className="goal-item">
                            <span className="goal-icon">✅</span>
                            <span>{getGoalDisplayName(goal)}</span>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                )}

                <div className="detail-section">
                  <h3>Additional Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Signature:</label>
                      <span>{selectedMember.signature || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Terms Accepted:</label>
                      <span>{selectedMember.termsAccepted ? 'Yes' : 'No'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="member-detail-actions">
              <button className="action-btn" onClick={closeMemberDetail}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Details Modal */}
      {showPaymentDetails && (
        <div className="payment-details-overlay" onClick={closePaymentDetails}>
          <div className="payment-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="payment-details-header">
              <h2>Payment Details</h2>
              <button className="close-btn" onClick={closePaymentDetails}>×</button>
            </div>
            
            <div className="payment-details-content">
              {isLoadingMemberPayments ? (
                <div className="loading-section">
                  <div className="loading-spinner"></div>
                  <p>Loading payment details...</p>
                </div>
              ) : selectedMemberPayments.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">💰</div>
                  <h3>No Payment History</h3>
                  <p>This member hasn't made any payments yet.</p>
                </div>
              ) : (
                <div className="payment-details-table-container">
                  <table className="payment-details-table">
                    <thead>
                      <tr>
                        <th>Payment Date</th>
                        <th>Amount</th>
                        <th>Payment Method</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedMemberPayments.map((payment, index) => (
                        <tr key={payment.id || index}>
                          <td>{new Date(payment.paymentDate).toLocaleDateString()}</td>
                          <td className="amount-cell">₹{payment.amount?.toLocaleString() || 0}</td>
                          <td>
                            <span className={`payment-method-badge ${payment.paymentMethod}`}>
                              {payment.paymentMethod}
                            </span>
                          </td>
                          <td>{payment.notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  <div className="payment-summary">
                    <div className="summary-item">
                      <span className="summary-label">Total Payments:</span>
                      <span className="summary-value">{selectedMemberPayments.length}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Total Amount:</span>
                      <span className="summary-value">
                        ₹{selectedMemberPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Last Payment:</span>
                      <span className="summary-value">
                        {selectedMemberPayments.length > 0 
                          ? new Date(selectedMemberPayments[0].paymentDate).toLocaleDateString()
                          : 'N/A'
                        }
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard; 